import { ENDPOINTS, TASK_TYPES } from 'features/daily-plan/constants';
import { HIRING_AUTHORITY_TAB_IDS } from 'features/inventory-profiles/constants';
import { CompaniesVirtualProps } from 'UI/components/organisms/inventoryProfiles/CompaniesTab/columns';
import { feeAgreementHiringAuthorityProfileColumns } from 'UI/components/organisms/inventoryProfiles/FeeAgreementTab/columns';
import { HiringAuthorityProfileJobOrderColumns } from 'UI/components/organisms/inventoryProfiles/JobOrdersTab/hiringAuthoritiesJOcolumns';
import { hiringAuthorityProfilePlacementsColumns } from 'UI/components/organisms/inventoryProfiles/PlacementsTab/columns';
import {
  HIRING_AUTHORITY_SENDOUTS_COLUMNS,
  SENDOUTS_RENDERERS,
  SENDOUTS_VIRTUAL_PROPS
} from 'UI/components/organisms/inventoryProfiles/SendoutsTab/columns';
import { ProfileTablesPreferenceKeys } from 'UI/components/organisms/inventoryProfiles/tabKeys';
import {
  formatTasksResponse,
  TASK_COLUMNS,
  TASKS_EXTRA_RENDERERS
} from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { Endpoints } from 'UI/constants/endpoints';
import { ENTITY_TYPE_ID_MAP, EntityType, PaymentMode } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { currencyFormatter, filterColumnsByFeatureFlag } from 'UI/utils';
import { formatResponseBuilder } from 'UI/utils/inventory';

const FEE_AGREEMENT_RENDERERS = {
  fee: value => {
    const feePercentage = `${value?.percentage ? `${value?.percentage}%` : ''}`;
    const isFlatPayment = value?.payment?.scheme?.id === PaymentMode.Flat;
    const feeAgreementValue = isFlatPayment ? currencyFormatter(value?.flatAmount) : feePercentage;

    return feeAgreementValue;
  }
};

const JOB_ORDER_KEYWORDS_DEFINITION = [
  {
    entry: 'title',
    valuesToAttach: [
      {
        name: 'title',
        value: 'title'
      },
      { name: 'id', value: 'id' }
    ]
  },
  {
    entry: 'specialty',
    valuesToAttach: [
      { name: 'industry', value: 'industry' },
      { name: 'title', path: 'specialty.title' }
    ]
  },
  {
    entry: 'jobOrderTypeTitle',
    valuesToAttach: [
      {
        name: 'title',
        path: 'jobOrderType.title'
      },
      {
        name: 'id',
        path: 'jobOrderType.id'
      }
    ]
  },
  {
    entry: 'compensation',
    valuesToAttach: [
      { name: 'maximum_compensation', value: 'maximumCompensation' },
      { name: 'minimum_compensation', value: 'minimumCompensation' },
      { name: 'good_compensation', value: 'goodCompensation' }
    ]
  },
  {
    entry: 'address',
    valuesToAttach: [
      { name: 'city', value: 'city' },
      { name: 'state', value: 'state' }
    ]
  },
  {
    entry: 'statusTitle',
    valuesToAttach: [
      {
        name: 'title',
        path: 'status.title'
      },
      {
        name: 'id',
        path: 'status.id'
      }
    ]
  },
  {
    entry: 'recruiterName',
    valuesToAttach: [
      {
        path: 'recruiter.name'
      }
    ]
  },
  {
    entry: 'companyName',
    valuesToAttach: [
      { name: 'id', path: 'company.id' },
      { name: 'name', path: 'company.name' }
    ]
  }
];

const JOB_ORDER_COLUMNS_WITH_FEATURE_FLAG = [
  {
    featureFlag: FeatureFlags.JobOrderColumnsHAProfile,
    columns: ['recruiterName', 'lastActivityDate', 'jobOrderTypeTitle', 'statusTitle']
  }
];

const JOB_ORDER_COLUMNS = filterColumnsByFeatureFlag(
  HiringAuthorityProfileJobOrderColumns,
  JOB_ORDER_COLUMNS_WITH_FEATURE_FLAG
);

const handleJobOrderResponseChange = response =>
  formatResponseBuilder(response)(JOB_ORDER_KEYWORDS_DEFINITION);

const PLACEMENTS_KEYWORDS_DEFINITION = [
  {
    entry: 'start_date',
    valuesToAttach: [
      {
        path: 'startDate'
      }
    ]
  },
  {
    entry: 'fee_amount',
    valuesToAttach: [{ path: 'feeAmount' }]
  },
  {
    entry: 'company_recruiter',
    valuesToAttach: [
      {
        path: 'companyRecruiters.fullNames'
      }
    ]
  },
  {
    entry: 'candidate_recruiter',
    valuesToAttach: [
      {
        path: 'candidateRecruiters.fullNames'
      }
    ]
  }
];

const handlePlacementsResponseChange = response =>
  formatResponseBuilder(response)(PLACEMENTS_KEYWORDS_DEFINITION);

export const HIRING_AUTHORITY_TABS_CONFIG = {
  [HIRING_AUTHORITY_TAB_IDS.Companies]: {
    endpoint: Endpoints.Companies,
    tablePreferencesKey: ProfileTablesPreferenceKeys.HiringAuthorityCompanies,
    DataTableProps: {
      apiVersion: 2,
      virtualProps: CompaniesVirtualProps,
      includes: 'lastActivityLog,city,specialty,type,keywordTags'
    }
  },
  [HIRING_AUTHORITY_TAB_IDS.Sendouts]: {
    endpoint: Endpoints.Sendouts,
    tablePreferencesKey: ProfileTablesPreferenceKeys.HiringAuthoritySendouts,
    extraRenderers: SENDOUTS_RENDERERS,
    DataTableProps: {
      apiVersion: 1,
      includes: 'interviews',
      virtualProps: SENDOUTS_VIRTUAL_PROPS
    },
    initialColumns: HIRING_AUTHORITY_SENDOUTS_COLUMNS,
    paramsBuilder: [
      {
        name: 'fields',
        value:
          'id,feeAmount,sentOn,trackingDate,interviewDate,statusId,statusTitle,statusStyle,statusLabel,joId,joTitle,companyId,companyName,caId,caFullName,caAccountableId,caAccountableInitials,caAccountableUserName,joAccountableId,joAccountableInitials,joAccountableUserName,typeId,typeTitle,typeStyle'
      }
    ]
  },
  [HIRING_AUTHORITY_TAB_IDS.JobOrders]: {
    endpoint: Endpoints.JobOrders,
    tablePreferencesKey: ProfileTablesPreferenceKeys.HiringAuthorityJobOrders,
    DataTableProps: {
      apiVersion: 1,
      formatResponse: handleJobOrderResponseChange,
      orderByOptions: { column: 'created_at', direction: 'desc' },
      includes: 'keywordTags'
    },
    initialColumns: JOB_ORDER_COLUMNS,
    paramsBuilder: [
      {
        name: 'fields',
        value:
          'id,title,lastActivityDate,statusId,statusTitle,statusStyle,statusIsSelectable,positionId,positionTitle,recruiterId,recruiterEmail,recruiterInitials,recruiterName,recruiterCoachId,recruiterCoachEmail,recruiterCoachName,cityId,cityTitle,stateId,stateTitle,stateTitle,specialtyId,specialtyTitle,industryId,industryTitle,maximumCompensation,minimumCompensation,goodCompensation,workTypeId,workTypeTitle,companyId,companyName,createdAt,statusId,statusTitle,recruiterName,jobOrderTypeTitle,jobOrderTypeId,lastActivityLogTitle,lastActivityLogRecruiter,lastActivityLogDate'
      }
    ]
  },
  [HIRING_AUTHORITY_TAB_IDS.Placements]: {
    endpoint: Endpoints.Placements,
    tablePreferencesKey: ProfileTablesPreferenceKeys.HiringAuthorityPlacement,
    initialColumns: hiringAuthorityProfilePlacementsColumns,
    DataTableProps: {
      formatResponse: handlePlacementsResponseChange
    },
    paramsBuilder: [
      {
        name: 'fields',
        value:
          'id,feeAmount,startDate,statusId,statusTitle,statusStyle,jobOrderId,jobOrderTitle,companyId,companyName,candidateRecruiters,companyRecruiters'
      }
    ]
  },
  [HIRING_AUTHORITY_TAB_IDS.FeeAgreements]: {
    endpoint: Endpoints.FeeAgreement,
    tablePreferencesKey: ProfileTablesPreferenceKeys.HiringAuthorityFeeAgreement,
    initialColumns: feeAgreementHiringAuthorityProfileColumns,
    DataTableProps: {},
    extraRenderers: FEE_AGREEMENT_RENDERERS,
    paramsBuilder: [
      {
        name: 'fields',
        value:
          'id,feePercentage,guaranteeDays,validatedDate,signedDate,haSignDate,createdAt,validatedDate,statusId,statusName,statusTitle,statusStyle,companyId,companyName,creatorId,creatorUserName,coachId,coachUserName,directorId,directorUserName,regionalId,regionalUserName,paymentSchemeTitle,paymentSchemeId,flatFeeAmount'
      }
    ]
  },
  [HIRING_AUTHORITY_TAB_IDS.ActionPlan]: {
    allowedTaskTypes: [
      TASK_TYPES.marketCandidate,
      TASK_TYPES.debriefCall,
      TASK_TYPES.preparationCall,
      TASK_TYPES.closingCall,
      TASK_TYPES.other
    ],
    DataTableProps: {
      apiVersion: 1,
      endpoint: ENDPOINTS.tasks,
      formatResponse: formatTasksResponse
    },
    entityType: EntityType.HiringAuthority,
    entityTypeId: ENTITY_TYPE_ID_MAP.hiringAuthority,
    extraRenderers: TASKS_EXTRA_RENDERERS,
    initialColumns: TASK_COLUMNS,
    tablePreferencesKey: ProfileTablesPreferenceKeys.HiringAuthorityTasks
  }
};
